type ValueOf<T> = T[keyof T];

export const TICKET_CATEGORY = {
  CHANGEABLE: 'changeable',
  UNCHANGEABLE: 'unchangeable'
} as const;

export const PROPOSAL_BUNDLE_CATEGORY = {
  TRANSPORT: 'transport',
  HOTEL: 'hotel',
  PACKAGE: 'package',
  OUTWARD: 'outward',
  HOMEWARD: 'homeward'
} as const;

export type TicketCategory = ValueOf<typeof TICKET_CATEGORY>;
export type PreferredTransportType = 'rail' | 'air';
export type ProposalBundleState = 'suggesting' | 'selected' | 'rejected';
export type ProposalBundleCategory = ValueOf<typeof PROPOSAL_BUNDLE_CATEGORY>;
export type SummaryItemCategory = Extract<
  ProposalBundleCategory,
  | typeof PROPOSAL_BUNDLE_CATEGORY.OUTWARD
  | typeof PROPOSAL_BUNDLE_CATEGORY.HOMEWARD
  | typeof PROPOSAL_BUNDLE_CATEGORY.HOTEL
>;
export type ForeignItinerary = 'one_way' | 'round_trip';

export const TICKET_CATEGORY_LABELS: Record<TicketCategory, string> = {
  [TICKET_CATEGORY.CHANGEABLE]: '変更可',
  [TICKET_CATEGORY.UNCHANGEABLE]: '変更不可'
} as const;

export const PROPOSAL_BUNDLE_STATE = {
  SUGGESTING: 'suggesting',
  SELECTED: 'selected',
  REJECTED: 'rejected'
} as const;

export const PROPOSAL_BUNDLE_STATE_LABELS: Record<ProposalBundleState, string> = {
  [PROPOSAL_BUNDLE_STATE.SUGGESTING]: '提案中',
  [PROPOSAL_BUNDLE_STATE.SELECTED]: '選択済み',
  [PROPOSAL_BUNDLE_STATE.REJECTED]: '予約しない'
} as const;

export const PROPOSAL_BUNDLE_CATEGORY_LABELS: Record<ProposalBundleCategory, string> = {
  [PROPOSAL_BUNDLE_CATEGORY.OUTWARD]: '行き',
  [PROPOSAL_BUNDLE_CATEGORY.HOMEWARD]: '帰り',
  [PROPOSAL_BUNDLE_CATEGORY.TRANSPORT]: '交通',
  [PROPOSAL_BUNDLE_CATEGORY.HOTEL]: 'ホテル',
  [PROPOSAL_BUNDLE_CATEGORY.PACKAGE]: 'パッケージ'
} as const;

export const TICKET_TYPE_FOREIGN = 'foreign' as const;

export type ProposalItem = {
  id: number;
  title: string | null;
  subtitle: string | null;
  departureTime: string | null;
  arrivalTime: string | null;
  departureDate: string | null;
  arrivalDate: string | null;
  flightName: string | null;
  isConnectionFlight: boolean;
  connectionSummary: string | null;
  seatClass: string | null;
  ticketType: string | null;
  foreignItinerary: ForeignItinerary | null;
  ticketCategory: TicketCategory | null;
  price: number | null;
  priceWithTax: number | null;
  priceText: string | null;
  aiHighlightTags: string[];
  isAir: boolean | null;
  isShinkansen: boolean | null;
  stayDays: number | null;
  breakfastExist: boolean | null;
  smoke: boolean | null;
  selected: boolean;
  segmentRouteLabel: string | null;
  airlineDisplay: string | null;
  roundTripDurationLabel: string | null;
  legDurationSummary: string | null;
  roundTripFlightId: string | null;
  foreignRoundTripDisplayKey: string;
};

export type ProposalBundle = {
  id: number;
  category: ProposalBundleCategory;
  state: ProposalBundleState;
  origin: string | null;
  destination: string | null;
  departureDate: string | null;
  returnDate: string | null;
  stayDays: number | null;
  preferredTransportType: PreferredTransportType | null;
  hasForeignFlightRoundTrip: boolean | null;
  hasForeignFlight: boolean | null;
  items: ProposalItem[];
};

export type HotelDetail = {
  name: string;
  address: string;
  images: string[];
  latitude: number;
  longitude: number;
  distance: number;
  nearestStationName: string | null;
  stationDistance: number;
  checkin: string;
  checkout: string;
  tel: string;
  cancelPolicy: string;
  refundable: boolean | null;
  propertyAmenities: string[] | null;
  roomAmenities: string[] | null;
  note: string | null;
  parkingInformation: string | null;
  checkinInstructions: string | null;
  specialCheckinInstructions: string | null;
};

export type DestLocation = {
  lat: number;
  lng: number;
};

export type HotelBedType = {
  description: string;
};

export type HotelPlanDetail = {
  roomName: string;
  planName: string | null;
  planDetail: string | null;
  price: number;
  priceWithTax: number;
  priceText: string;
  smoke: boolean | null;
  bedTypes: HotelBedType[] | null;
  checkinTime: string | null;
};

export type HotelDetailData = {
  hotel: HotelDetail;
  destLocation: DestLocation | null;
  plan: HotelPlanDetail;
};
